import { Body, Controller, Delete, Get, Param, Post, UseGuards } from '@nestjs/common';
import { OltService } from './services/olt.service';
import { PortService } from './services/port.service';
import { OltDB } from './services/olt_database.service';
import { GponService } from './services/gpon.service';
import { ServicePortService } from './services/serviceport.service';
import { GponXProfiles, OntParams, OntsBySerialNum, ServicePort } from '@ip6m/types';
import { ApiOperation, ApiParam, ApiTags } from '@nestjs/swagger';
import { IsAdminGuard, IsAuthenticatedGuard } from '@ip6m/server/auth';
import { PublicUserService } from './services/user.service';

@ApiTags('snmp-api')
@Controller('v1')
@UseGuards(IsAuthenticatedGuard)
export class CoreController {
  constructor(
    private readonly userService: PublicUserService,
    private readonly oltService: OltService,
    private readonly portService: PortService,
    private readonly oltDB: OltDB,
    private readonly gponService: GponService,
    private readonly servicePortService: ServicePortService) {}

  @Get('people')
  getUsers() {
        return this.userService.getUsers();
  }  

  @Post('setpassword')
  @UseGuards(IsAdminGuard)
  setPassword(@Body() data) {
    return this.userService.setPassword(data);
  }  
  
  @Get('/olt')
  @ApiOperation({ summary: 'Get connfigured OLT-s' })
  getOltList() {
    return this.oltDB.getOlts;
  }

  @ApiOperation({ summary: 'Get OLT name'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid')
  getOlt(@Param() params) {
    return this.oltDB.getOltName(params.oltid);
  }

  @ApiOperation({ summary: 'Get OLT system mib'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/system')
  getSystem(@Param() params) {
    return this.oltService.getSysinfo(params.oltid);
  }

  @ApiOperation({ summary: 'Get OLT frame info'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/frame')
  getFrame(@Param() params) {
    return this.oltService.getFrameInfo(params.oltid);
  }

  @ApiOperation({ summary: 'Get OLT slots'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/slot')
  getSlot(@Param() params) {
    return this.oltService.getSlots(params.oltid);
  }

  @ApiOperation({ summary: 'Get OLT ports'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/port')
  getPort(@Param() params) {
    return this.portService.getPorts(params.oltid);
  }

  @ApiOperation({ summary: 'Get GPON ports'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/gpon/port')
  getGponPorts(@Param() params) {
    return this.gponService.getPorts(params.oltid);
  }

  @ApiOperation({ summary: 'Get one GPON port by ifindex'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @ApiParam({
    name: 'ifIndex',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/gpon/port/:ifIndex')
  getGponPort(@Param() params) {
    return this.gponService.getPort(params.oltid, params.ifIndex);
  }

  @ApiOperation({ summary: 'Get lineprofiles by OLT'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/gpon/lineprofile') 
  getLineProfiles(@Param() params){
    return this.gponService.getXProfiles(params.oltid);
  }

  @ApiOperation({ summary: 'Get serviceprofiles by OLT'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/gpon/serviceprofile') 
  getServiceProfiles(@Param() params){
    return this.gponService.getXProfiles(params.oltid, 'service');
  }

  @ApiOperation({ summary: 'Get activated ONT-s by OLT'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/gpon/ont')
  getGponOnt(@Param() params): Promise<OntsBySerialNum> {
    return this.gponService.getOnts(params.oltid);
  }

  @ApiOperation({ summary: 'Get autofound ONT-s by OLT'})
  @ApiParam({
        name: 'oltid',
        type: 'number',
        example: '0'
  })
  @Get('/olt/:oltid/gpon/ont/autofound')
  getGponAutofoundOnt(@Param() params) {
    return this.gponService.getAutofoundOnts(params.oltid);
  }

  @ApiOperation({ summary: 'Get ONT-s on port identified by ifindex'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @ApiParam({
    name: 'ifIndex',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/gpon/ont/:ifIndex')
  getGponOntIfIndex(@Param() params): Promise<OntsBySerialNum> {
    return this.gponService.getOnts(params.oltid, params.ifIndex);
  }

  @ApiOperation({ summary: 'Get autofound ONT-s on port identified by ifindex'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @ApiParam({
    name: 'ifIndex',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/gpon/ont/autofound/:ifIndex')
  getGponAutofoundOntIfIndex(@Param() params) {
    return this.gponService.getAutofoundOnts(params.oltid, params.ifIndex);
  }

  @Post('/olt/:oltid/gpon/ont/activate/:ifIndex/:macAddress')
  activateOnt(@Param() params, @Body() profiles: GponXProfiles) {
    return this.gponService.activateOnt(params.oltid, params.ifIndex, params.macAddress, profiles);
  }

  @Get('/olt/:oltid/gpon/ont/deactivate/:ifIndex/:ontId')
  deActivateOnt(@Param() params) {
    return this.gponService.deActivateOnt(params.oltid, params.ifIndex, params.ontId);
  }

  @ApiOperation({ summary: 'Preload serviceports into backend'})
  @ApiParam({
    name: 'oltid',
    type: 'number',
    example: '0'
  })
  @Get('/olt/:oltid/serviceportinit')
  servicePortInit(@Param() params): Promise<number> {
    return this.servicePortService.initServicePorts(params.oltid);
  }

  @Get('/olt/:oltid/serviceport')
  servicePort(@Param() params) {
    return this.servicePortService.getServicePorts(params.oltid);
  }

  @Post('/olt/:oltid/serviceport/:frame/:slot/:port/:ontid')
  addServicePort(@Param() params, @Body() body: ServicePort) {
    const ontParams: OntParams = {
      frame: parseInt(params.frame),
      slot: parseInt(params.slot),
      port: parseInt(params.port),
      ontId: parseInt(params.ontid)
    }
    return this.servicePortService.addServicePort(params.oltid, ontParams, body);
  }


  @Get('/olt/:oltid/serviceport/:frame/:slot/:port/:ontid')
  servicePortByOnt(@Param() params) {
    const ontParams: OntParams = {
      frame: params.frame,
      slot: params.slot,
      port: params.port,
      ontId: params.ontid
    }
    return this.servicePortService.getServicePorts(params.oltid, ontParams);
  }

  @Delete('/olt/:oltid/serviceport/:frame/:slot/:port/:ontId/:i')
  deleteServicePort(@Param() params) {
    return this.servicePortService.deleteServicePort(params);
  }

  @Get('/olt/:oltid/iptrafficparamtable')
  ipTrafficParamTable() {
    return this.servicePortService.getIpTrafficParamTable();
  }

  @Get('/olt/:oltid/vlan')
  vlan(@Param() params) {
    return this.oltService.getVlans(params.oltid);
  }



}
